import React from "react";
import { toast } from "sonner";

import AssignRequestProductToProject from "./components/product-request/AssignRequestedProductToProjectModal";
import ProductRequestSheet from "./components/product-request/ProductRequestSheet";
import ApproveReturnRequestModal from "./components/return-request/ApproveReturnRequestModal";
import TransferToolsModal from "./components/transfer-request/TransferToolsModal";
import AwaitingRequestTable from "./table/AwaitingTable";
import ProductRequestTable from "./table/ProductRequestTable";
import ReturnRequestTable from "./table/ReturnRequestTable";
import TransferRequestTable from "./table/TransferRequestTable";

import { AlertDialogWithTextArea } from "@/components/alerts/AlertDialogWithTextArea";
import FetchWrapper from "@/components/reusables/FetchWrapper";
import CustomHeader from "@/components/reusables/Headers.tsx";
import { baseFilterParams } from "@/constants/constants";
import { requestTabs } from "@/constants/tabs.constant";
import { ToolsConstants } from "@/constants/tools.constant";
import DataTableSlotContextProvider from "@/context/DataTableSlot";
import { useGetParams } from "@/hooks/useGetParams";
import { PaginationMeta, usePagination } from "@/hooks/usePagination";
import {
  IAwaitingRequest,
  IProductRequest,
  IProductRequestDetail,
  IReturnRequest,
  IReturnRequestItem,
  ITransferRequest,
  TBase,
} from "@/interfaces/tools.interface";
import {
  useGetAwaitingRequestQuery,
  useGetProductRequestQuery,
  useGetReturnRequestQuery,
  useGetTransferToolsQuery,
  useRejectReturnRequestMutation,
} from "@/redux/api/toolsQuery";
import { validateResponse } from "@/utils/helper-functions/form.helper";

const RequestPage = (): JSX.Element => {
  const {
    INDEX_CONTENT_TEXT,
    REQUEST: { RETURN_REQUEST },
  } = ToolsConstants;
  const [activeTab, setActiveTab] = React.useState(requestTabs[0].value);
  const [approveModal, setApproveModal] = React.useState<TBase<
    IReturnRequestItem[]
  > | null>(null);

  const [transferModalOpen, setTransferModalOpen] = React.useState(false);
  const [assignRequestProductModal, setAssignRequestProductModal] =
    React.useState<TBase<IProductRequestDetail[]> | null>(null);
  const [rejectReturnRequest, setRejectReturnRequest] = React.useState<{
    isOpen: boolean;
    id: string;
    requestBy: string;
  } | null>(null);
  const [productRequest, setProductRequest] =
    React.useState<TBase<IProductRequest> | null>(null);

  const filters = useGetParams(baseFilterParams);

  const { data: transferToolsData, ...transferDataRest } =
    useGetTransferToolsQuery(
      {
        filters,
      },
      {
        skip: activeTab !== requestTabs[2].value || !filters.includes("limit="),
      },
    );

  const { data: returnRequestData, ...returnRequestDataRest } =
    useGetReturnRequestQuery(
      {
        filters,
      },
      {
        skip: !filters.includes("limit=") || activeTab !== requestTabs[0].value,
      },
    );

  const { data: awaitingRequestData, ...awaitingRequestDataRest } =
    useGetAwaitingRequestQuery(
      {
        filters,
      },
      {
        skip: !filters.includes("limit=") || activeTab !== requestTabs[3].value,
      },
    );

  const { data: productRequestData, ...productRequestDataRest } =
    useGetProductRequestQuery(
      {
        filters,
      },
      {
        skip: !filters.includes("limit=") || activeTab !== requestTabs[1].value,
      },
    );

  const [alterRejectReturnRequest] = useRejectReturnRequestMutation();

  const response = React.useMemo(() => {
    let res;
    let response;
    let pagination;

    switch (activeTab) {
      case requestTabs[0].value:
        response = returnRequestData?.data;
        pagination = returnRequestData?.pagination;
        res = returnRequestDataRest;
        break;
      case requestTabs[1].value:
        response = productRequestData?.data;
        pagination = productRequestData?.pagination;
        res = productRequestDataRest;
        break;
      case requestTabs[2].value:
        response = transferToolsData?.data;
        pagination = transferToolsData?.pagination;
        res = transferDataRest;
        break;
      case requestTabs[3].value:
        response = awaitingRequestData?.data;
        pagination = awaitingRequestData?.pagination;
        res = awaitingRequestDataRest;
        break;
      default:
        response = productRequestData?.data;
        pagination = productRequestData?.pagination;
        res = productRequestDataRest;
        break;
    }

    return {
      response,
      pagination,
      rest: res,
    };
  }, [
    transferToolsData,
    transferDataRest,
    activeTab,
    productRequestData,
    productRequestDataRest,
    awaitingRequestData,
    awaitingRequestDataRest,
    returnRequestData,
    returnRequestDataRest,
  ]);

  const pagination = usePagination(response?.pagination as PaginationMeta);

  const toggleTransferModal = (): void => {
    setTransferModalOpen(!transferModalOpen);
  };

  const renderTableAccordingToTab = (): JSX.Element => {
    switch (activeTab) {
      case requestTabs[0].value:
        return (
          <ReturnRequestTable
            data={(response?.response as IReturnRequest[]) ?? []}
            toggleApproveModal={setApproveModal}
            pagination={pagination}
            toggleRejectModal={setRejectReturnRequest}
          />
        );
      case requestTabs[1].value:
        return (
          <ProductRequestTable
            data={(response?.response as IProductRequest[]) ?? []}
            pagination={pagination}
            toggleAssignModal={setAssignRequestProductModal}
            onRowClick={setProductRequest}
          />
        );
      case requestTabs[2].value:
        return (
          <TransferRequestTable
            data={(response?.response as ITransferRequest[]) ?? []}
            toggleTransferModal={toggleTransferModal}
            pagination={pagination}
          />
        );
      case requestTabs[3].value:
        return (
          <AwaitingRequestTable
            data={(response?.response as IAwaitingRequest[]) ?? []}
            pagination={pagination}
          />
        );
      default:
        return (
          <ReturnRequestTable
            data={(response?.response as IReturnRequest[]) ?? []}
            toggleApproveModal={setApproveModal}
            pagination={pagination}
            toggleRejectModal={setRejectReturnRequest}
          />
        );
    }
  };

  return (
    <DataTableSlotContextProvider>
      <CustomHeader
        title={INDEX_CONTENT_TEXT.REQUEST}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={requestTabs}
      />
      <FetchWrapper {...response?.rest} isTable>
        {renderTableAccordingToTab()}
      </FetchWrapper>
      {approveModal?.isOpen && (
        <ApproveReturnRequestModal
          toggleModal={() => setApproveModal(null)}
          {...approveModal}
        />
      )}
      {transferModalOpen && (
        <TransferToolsModal
          isOpen={transferModalOpen}
          toggleModal={toggleTransferModal}
        />
      )}
      {assignRequestProductModal?.isOpen && (
        <AssignRequestProductToProject
          toggleModal={() => setAssignRequestProductModal(null)}
          {...assignRequestProductModal}
        />
      )}
      {rejectReturnRequest?.isOpen && (
        <AlertDialogWithTextArea
          isOpen={rejectReturnRequest.isOpen}
          toggleDialog={() => setRejectReturnRequest(null)}
          label={RETURN_REQUEST.REJECT_REQUEST.INPUT_LABEL}
          placeholder={RETURN_REQUEST.REJECT_REQUEST.PLACEHOLDER}
          onConfirm={async (value) => {
            const response = await alterRejectReturnRequest({
              id: rejectReturnRequest.id,
              body: {
                remarks: value,
              },
            });
            validateResponse({
              response,
              onSuccess: () => {
                setRejectReturnRequest(null);
                toast.success(RETURN_REQUEST.REJECT_REQUEST.SUCCESS_MESSAGE);
              },
            });
          }}
          confirmBtnLabel={RETURN_REQUEST.REJECT_REQUEST.BTN_LABEL}
          subtitle={
            RETURN_REQUEST.REJECT_REQUEST.SUB_TITLE +
            rejectReturnRequest?.requestBy +
            "?"
          }
        />
      )}
      {productRequest?.isOpen && (
        <ProductRequestSheet
          toggleProductRequestSheet={() => setProductRequest(null)}
          {...productRequest}
        />
      )}
    </DataTableSlotContextProvider>
  );
};

export default RequestPage;
